import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails } from '../../redux/userRelated/userHandle';
import { useNavigate, useParams } from 'react-router-dom'
import { 
    Box, Button, Collapse, Table, TableBody, TableHead, Typography, 
    Paper, Grid, Container, Avatar, Divider, Chip, Fade,
    IconButton, Tooltip, TableCell, TableRow
} from '@mui/material';
import { 
    KeyboardArrowDown, 
    KeyboardArrowUp,
    Person as PersonIcon,
    Badge as BadgeIcon,
    School as SchoolIcon,
    Class as ClassIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    AddCircleOutline as AddIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { calculateOverallAttendancePercentage, calculateSubjectAttendancePercentage, groupAttendanceBySubject } from '../../components/attendanceCalculator';
import CustomPieChart from '../../components/CustomPieChart'
import styled, { keyframes } from 'styled-components';
import { formatNepaliDate } from '../../utils/nepaliDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 20px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 15px 35px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const InfoRow = styled(Box)`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const TeacherViewStudent = () => {
    const navigate = useNavigate()
    const params = useParams()
    const dispatch = useDispatch();
    const { currentUser, userDetails, response, loading, error } = useSelector((state) => state.user);

    const address = "Student"
    const studentID = params.id
    const teachSubject = currentUser?.teachSubject?.subName
    const teachSubjectID = currentUser?.teachSubject?._id

    useEffect(() => {
        dispatch(getUserDetails(studentID, address));
    }, [dispatch, studentID]);

    const [sclassName, setSclassName] = useState('');
    const [studentSchool, setStudentSchool] = useState('');
    const [subjectMarks, setSubjectMarks] = useState('');
    const [subjectAttendance, setSubjectAttendance] = useState([]);
    const [openStates, setOpenStates] = useState({});

    const handleOpen = (subId) => {
        setOpenStates((prevState) => ({
            ...prevState,
            [subId]: !prevState[subId],
        }));
    };

    useEffect(() => {
        if (userDetails) {
            setSclassName(userDetails.sclassName || '');
            setStudentSchool(userDetails.school || '');
            setSubjectMarks(userDetails.examResult || '');
            setSubjectAttendance(userDetails.attendance || []);
        }
    }, [userDetails]);

    const overallAttendancePercentage = calculateOverallAttendancePercentage(subjectAttendance);
    const overallAbsentPercentage = 100 - overallAttendancePercentage;

    const chartData = [
        { name: 'Present', value: overallAttendancePercentage },
        { name: 'Absent', value: overallAbsentPercentage }
    ];

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar 
                                    src={userDetails?.photo ? `${process.env.REACT_APP_BASE_URL}/${userDetails.photo}` : undefined}
                                    sx={{ width: 80, height: 80, bgcolor: '#6366f1', fontSize: '2rem', border: '4px solid rgba(255,255,255,0.2)' }}
                                >
                                    {userDetails?.name?.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em', mb: 0.5 }}>
                                        {userDetails?.name || "Student Profile"}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip 
                                            label={`Roll No: ${userDetails?.rollNum || "N/A"}`} 
                                            size="small" 
                                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} 
                                        />
                                        <Chip 
                                            label={`Class: ${sclassName?.sclassName || "N/A"}`} 
                                            size="small" 
                                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} 
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Left Column: Stats & Charts */}
                    <Grid item xs={12} md={4}>
                        <PremiumCard sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <TimelineIcon sx={{ color: '#6366f1' }} />
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                    Attendance Overview
                                </Typography>
                            </Box>
                            
                            <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CustomPieChart data={chartData} />
                            </Box>
                            
                            <Divider sx={{ my: 3 }} />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    Overall Attendance
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 800 }}>
                                    {overallAttendancePercentage.toFixed(2)}%
                                </Typography>
                            </Box>
                        </PremiumCard>
                    </Grid>

                    {/* Right Column: Detailed Information */}
                    <Grid item xs={12} md={8}>
                        {/* Attendance Details for Teacher's Subject */}
                        <PremiumCard>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}>
                                    <SchoolIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                        Subject Performance: {teachSubject}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        Detailed breakdown of attendance and academic results for your subject.
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ p: 0 }}>
                                {subjectAttendance && Array.isArray(subjectAttendance) && subjectAttendance.length > 0 ? (
                                    Object.entries(groupAttendanceBySubject(subjectAttendance)).map(([subName, { present, allData, subId, sessions }], index) => {
                                        if (subName === teachSubject) {
                                            const subjectAttendancePercentage = calculateSubjectAttendancePercentage(present, sessions);
                                            return (
                                                <Box key={index}>
                                                    <Table>
                                                        <TableHead>
                                                            <TableRow>
                                                                <StyledTableCell>Present</StyledTableCell>
                                                                <StyledTableCell>Total Sessions</StyledTableCell>
                                                                <StyledTableCell>Percentage</StyledTableCell>
                                                                <StyledTableCell align="right">Details</StyledTableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{present}</TableCell>
                                                                <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{sessions}</TableCell>
                                                                <TableCell>
                                                                    <Chip 
                                                                        label={`${subjectAttendancePercentage}%`} 
                                                                        size="small" 
                                                                        sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 700 }} 
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton onClick={() => handleOpen(subId)} sx={{ color: '#6366f1' }}>
                                                                        {openStates[subId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={{ p: 0 }} colSpan={4}>
                                                                    <Collapse in={openStates[subId]} timeout="auto" unmountOnExit>
                                                                        <Box sx={{ p: 3, bgcolor: '#f1f5f9' }}>
                                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#475569' }}>
                                                                                Daily Attendance Log
                                                                            </Typography>
                                                                            <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                                                                                <Table size="small">
                                                                                    <TableHead>
                                                                                        <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                                                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Status</TableCell>
                                                                                        </TableRow>
                                                                                    </TableHead>
                                                                                    <TableBody>
                                                                                        {allData.map((data, idx) => {
                                                                                            const date = new Date(data.date);
                                                                                            const dateString = date.toString() !== "Invalid Date" ? formatNepaliDate(date) : "Invalid Date";
                                                                                            return (
                                                                                                <TableRow key={idx}>
                                                                                                    <TableCell>{dateString}</TableCell>
                                                                                                    <TableCell align="right">
                                                                                                        <Chip 
                                                                                                            label={data.status} 
                                                                                                            size="small" 
                                                                                                            sx={{ 
                                                                                                                bgcolor: data.status === 'Present' ? '#ecfdf5' : '#fef2f2',
                                                                                                                color: data.status === 'Present' ? '#059669' : '#dc2626',
                                                                                                                fontWeight: 700,
                                                                                                                minWidth: 70
                                                                                                            }} 
                                                                                                        />
                                                                                                    </TableCell>
                                                                                                </TableRow>
                                                                                            );
                                                                                        })}
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </Paper>
                                                                        </Box>
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })
                                ) : (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography color="textSecondary">No attendance records found for this subject.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </PremiumCard>

                        {/* Marks Details */}
                        <PremiumCard>
                            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <Avatar sx={{ bgcolor: '#fff7ed', color: '#f97316' }}>
                                    <AssignmentIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                        Academic Results
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                        Marks obtained by the student in your subject exams.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ p: 0 }}>
                                {subjectMarks && Array.isArray(subjectMarks) && subjectMarks.length > 0 ? (
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>Subject</StyledTableCell>
                                                <StyledTableCell align="right">Marks Obtained</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {subjectMarks.map((result, index) => {
                                                if (result.subName?.subName === teachSubject) {
                                                    return (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{ fontWeight: 600 }}>{result.subName.subName}</TableCell>
                                                            <TableCell align="right">
                                                                <Typography sx={{ fontWeight: 800, color: '#3b82f6' }}>
                                                                    {result.marksObtained}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography color="textSecondary">No marks recorded yet.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </PremiumCard>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default TeacherViewStudent;